/**
 * Home Maintenance selectors.
 */

import { createSelector } from 'reselect';
import { MaintenanceState, ServiceRecord, ApplianceResearchState, emptyResearchState } from './ducks';
import { Appliance, Todo } from '@/infra/types/domain';

export type StateWithMaintenance = { maintenance: MaintenanceState } | MaintenanceState;

function getMaintenanceState(state: StateWithMaintenance): MaintenanceState {
  return 'maintenance' in state && 'appliances' in (state as any).maintenance
    ? (state as { maintenance: MaintenanceState }).maintenance
    : (state as MaintenanceState);
}

export const selectAppliances = createSelector(
  [(state: StateWithMaintenance) => getMaintenanceState(state).appliances],
  (appliances): Appliance[] => appliances
);

/**
 * Plain (non-memoized) read of the loaded appliance list. Used by non-component
 * consumers — e.g. the equipment @mention provider — that read the array once
 * per call and gain nothing from reselect memoization.
 */
export const selectLoadedAppliances = (state: StateWithMaintenance): Appliance[] =>
  getMaintenanceState(state).appliances;

export const selectServiceRecords = createSelector(
  [(state: StateWithMaintenance) => getMaintenanceState(state).serviceRecords],
  (records): ServiceRecord[] => records
);

export const selectMaintenanceLoading = (state: StateWithMaintenance): boolean => {
  return getMaintenanceState(state).loading;
};

export const selectMaintenanceError = (state: StateWithMaintenance): string | null => {
  return getMaintenanceState(state).error;
};

export const selectApplianceById = (
  state: StateWithMaintenance,
  id: string,
): Appliance | undefined => {
  return getMaintenanceState(state).appliances.find((a) => a.id === id);
};

const EMPTY_TODOS: Todo[] = [];

export const selectApplianceTodos = (
  state: StateWithMaintenance,
  applianceId: string,
): Todo[] => {
  return getMaintenanceState(state).applianceTodos[applianceId] ?? EMPTY_TODOS;
};

export const selectApplianceTodosLoading = (state: StateWithMaintenance): boolean => {
  return getMaintenanceState(state).applianceTodosLoading;
};

export const selectApplianceResearch = (
  state: StateWithMaintenance,
  applianceId: string,
): ApplianceResearchState => {
  return getMaintenanceState(state).research[applianceId] ?? emptyResearchState;
};
